import { hostVetrina } from './marca';
import { simulatoreAttivo } from './simulatore';

/**
 * The authentication seam (M5). The read path (connector → API → CloudClient)
 * is built; what stands between it and live data is a token. This module is the
 * boundary the rest of the app talks to, so the heavy auth machinery
 * (aws-amplify + shared-react-auth) can be added and lazy-loaded behind it
 * without the demo path ever importing it.
 *
 * Two decisions live here, both pure and testable:
 *
 * - **Is auth configured?** Only when the pool + client id are present in the
 *   build. Absent (always on the published demo bundle) → the app stays the
 *   gated simulator demo, and nothing below can override that.
 * - **Does THIS host require a login?** A per-installer host does, and so does
 *   a developer's own machine once the build is configured. The company's
 *   public showcase hosts never do (ADR-059 §4/§5 — the demo runs without
 *   sign-in), and neither does an explicit request for `/simulatore`.
 *
 * And one seam: the **token provider**. The auth layer (M5.2) sets it on
 * sign-in; the `CloudClient` (M5.3) reads it. Kept here, decoupled from Amplify,
 * so a caller needs a token without importing the auth stack.
 */

export interface ConfigAuth {
  userPoolId: string;
  userPoolClientId: string;
  /** Hosted-UI domain: when present the front door is the pool's managed login
   *  (ADR-061 s8): one "Continua" button, a top-level redirect, and straight
   *  back when a session at a sibling product already exists. Optional so an
   *  unconfigured build stays exactly what it was. */
  cognitoDomain?: string;
}

/** The Cognito config from the build, or null when this build has no auth. */
export function configAuth(): ConfigAuth | null {
  const userPoolId = import.meta.env.REACT_APP_USER_POOL_ID;
  const userPoolClientId = import.meta.env.REACT_APP_CLIENT_ID;
  if (!userPoolId || !userPoolClientId) return null;
  const cognitoDomain = import.meta.env.REACT_APP_COGNITO_DOMAIN;
  return cognitoDomain ? { userPoolId, userPoolClientId, cognitoDomain } : { userPoolId, userPoolClientId };
}

/** Base URL of the ema-comfort API (custom domain + `/comfort` base path),
 *  from the build. Injected per environment by deploy-web (M5.4). */
export function apiBaseUrl(): string {
  return import.meta.env.REACT_APP_API_URL ?? '';
}

export interface OverrideModo {
  /** `?cloud=1` — force cloud mode where it would not otherwise apply. */
  forzaCloud?: boolean;
  /** The simulator was asked for explicitly (`/simulatore`, or `?demo=1`). */
  forzaDemo?: boolean;
}

/**
 * Whether a login must be shown, given the config, the host, and any explicit
 * override. Pure so the decision is testable without the DOM or a build.
 *
 * The rule, in the order it is applied:
 *
 * 1. **No config, no login.** A build without a pool + client id has no
 *    backend to authenticate against; it is the simulator and nothing else.
 *    Every published demo bundle is this, and no override can change it.
 * 2. **An explicit simulator request wins.** Whoever asked for `/simulatore`
 *    gets the simulator, on any host — that is the whole point of having a URL
 *    for it.
 * 3. **A public showcase host is always the simulator** (ADR-059 §4/§5).
 * 4. **Everything else that is configured requires a login** — an installer
 *    host, and a developer's own machine.
 *
 * 🔁 Point 4 INVERTS what a configured `localhost` used to do. It used to be
 * grouped with the showcase hosts and needed `?cloud=1` to show real data,
 * which meant the default view of a fully wired dev build was the simulator —
 * indistinguishable, at a glance, from the app not working. A build carrying a
 * pool, a client id and an API URL was pointed at a real backend deliberately;
 * showing it mock data by default hid whether any of that was true. `?cloud=1`
 * still works and is now simply redundant.
 */
export function richiedeLogin(
  config: ConfigAuth | null,
  hostname: string,
  { forzaCloud = false, forzaDemo = false }: OverrideModo = {},
): boolean {
  if (!config) return false;
  if (forzaCloud) return true;
  if (forzaDemo) return false;
  return !hostVetrina(hostname);
}

/** The `?cloud=1` dev override: force cloud mode where it would not apply.
 *  Inert on the demo build (no config). Kept because the lab runbook and
 *  bookmarks use it; on a developer host it now changes nothing. */
export function forzaCloud(search: string): boolean {
  return new URLSearchParams(search).get('cloud') === '1';
}

/**
 * Whether this URL asks for the simulator: the `/simulatore` route itself, or
 * `?demo=1` anywhere. The route counts so the configurator is reachable
 * without signing in — otherwise the only way to reach the demo on a
 * configured host would be through a login, which defeats the point.
 *
 * This is the ENTRY POINT only. It cannot keep the app in the simulator once
 * you navigate away, because in-app links drop the query string; that job
 * belongs to the remembered mode in `simulatore.ts`.
 */
export function forzaDemo(pathname: string, search: string): boolean {
  if (pathname === '/simulatore' || pathname.startsWith('/simulatore/')) return true;
  return new URLSearchParams(search).get('demo') === '1';
}

export interface Posizione {
  hostname: string;
  pathname: string;
  search: string;
}

/**
 * The whole decision from one location plus the remembered simulator mode.
 * Pure — every input is an argument — and the single rule two different
 * readers share: the router-aware gate in `App`, and `richiedeLoginQui()`
 * below for callers that only have `window`.
 */
export function richiedeLoginPer(
  config: ConfigAuth | null,
  { hostname, pathname, search }: Posizione,
  simulatore: boolean,
): boolean {
  return richiedeLogin(config, hostname, {
    forzaCloud: forzaCloud(search),
    forzaDemo: simulatore || forzaDemo(pathname, search),
  });
}

/** Convenience for callers outside the router: reads the live config, the
 *  window's location, and the remembered simulator mode. */
export function richiedeLoginQui(): boolean {
  if (typeof window === 'undefined') return false;
  const { hostname, pathname, search } = window.location;
  return richiedeLoginPer(configAuth(), { hostname, pathname, search }, simulatoreAttivo());
}

// ── the sign-out seam ─────────────────────────────────────────────────────

export type Esci = () => Promise<void>;

let esci: Esci | null = null;

/** The auth layer registers its sign-out here while a session exists; the
 *  settings page shows "Esci" only then (the demo never has one). */
export function impostaEsci(fn: Esci | null): void {
  esci = fn;
}

export function esciDisponibile(): boolean {
  return esci !== null;
}

/** Sign out. With the hosted front door this is a round trip through the
 *  pool's /logout: a sign-out on the whole pool, not just this app. */
export async function esciOra(): Promise<void> {
  if (esci) await esci();
}

// ── the token seam ────────────────────────────────────────────────────────

export type ProviderToken = () => Promise<string | null>;

let providerToken: ProviderToken | null = null;

/** The auth layer registers its token source here on sign-in (M5.2). */
export function impostaProviderToken(p: ProviderToken | null): void {
  providerToken = p;
}

/** The current caller's bearer token, or null when signed out / demo. The
 *  CloudClient reads this (M5.3). */
export async function tokenCorrente(): Promise<string | null> {
  return providerToken ? providerToken() : null;
}

/** Test seam: forget any registered provider. */
export function _reset(): void {
  providerToken = null;
}
